import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Spaces (kategorie/wątki w community)
export const spacesApi = {
  list: (communityId) => axios.get(`${API_URL}/spaces/community/${communityId}`),
  create: (communityId, payload) => axios.post(`${API_URL}/spaces/community/${communityId}`, payload),
  update: (spaceId, payload) => axios.patch(`${API_URL}/spaces/${spaceId}`, payload),
  remove: (spaceId) => axios.delete(`${API_URL}/spaces/${spaceId}`),
};

// Communities
export const communitiesApi = {
  list: (skip = 0, limit = 20) =>
    axios.get(`${API_URL}/communities?skip=${skip}&limit=${limit}`),
  get: (id) =>
    axios.get(`${API_URL}/communities/${id}`),
  // Community usera bazujące na SSO org (Sorto-CRM). 404 jeśli żadnej nie ma.
  mine: () =>
    axios.get(`${API_URL}/communities/my`),
  myRole: (id) =>
    axios.get(`${API_URL}/communities/${id}/my-role`),
  update: (id, payload) =>
    axios.patch(`${API_URL}/communities/${id}`, payload),
  create: (data) =>
    axios.post(`${API_URL}/communities`, data),
  join: (id) =>
    axios.post(`${API_URL}/communities/${id}/join`),
  // opts: { skip, limit, search, role, sort } — sort: name|joined|aura|posts
  getMembers: (id, opts = {}) => {
    const params = { skip: 0, limit: 50, ...opts };
    return axios.get(`${API_URL}/communities/${id}/members`, { params });
  },
};

// Courses
export const coursesApi = {
  list: (skip = 0, limit = 20, communityId = null) => {
    let url = `${API_URL}/courses?skip=${skip}&limit=${limit}`;
    if (communityId) url += `&community_id=${communityId}`;
    return axios.get(url);
  },
  get: (id) => 
    axios.get(`${API_URL}/courses/${id}`),
  create: (data) => 
    axios.post(`${API_URL}/courses`, data),
  enroll: (id) => 
    axios.post(`${API_URL}/courses/${id}/enroll`),
  getMy: () => axios.get(`${API_URL}/courses/my`),
  update: (id, data) => axios.put(`${API_URL}/courses/${id}`, data),
  listLessons: (id) => axios.get(`${API_URL}/courses/${id}/lessons`),
  createLesson: (id, data) => axios.post(`${API_URL}/courses/${id}/lessons`, data),
  updateLesson: (cid, lid, data) => axios.put(`${API_URL}/courses/${cid}/lessons/${lid}`, data),
  deleteLesson: (cid, lid) => axios.delete(`${API_URL}/courses/${cid}/lessons/${lid}`),
  completeLesson: (cid, lid) => axios.post(`${API_URL}/courses/${cid}/lessons/${lid}/complete`),
  getProgress: (id) => axios.get(`${API_URL}/courses/${id}/progress`),
};

// Events
export const eventsApi = {
  list: (skip = 0, limit = 20, communityId = null) => {
    let url = `${API_URL}/events?skip=${skip}&limit=${limit}`;
    if (communityId) url += `&community_id=${communityId}`;
    return axios.get(url);
  },
  get: (id) => 
    axios.get(`${API_URL}/events/${id}`),
  create: (data) => 
    axios.post(`${API_URL}/events`, data),
  register: (id) => 
    axios.post(`${API_URL}/events/${id}/register`),
};

// Posts
export const postsApi = {
  list: (communityId, skip = 0, limit = 20, spaceId = null, type = null, sort = 'recent') => {
    let url = `${API_URL}/posts?community_id=${communityId}&skip=${skip}&limit=${limit}&sort=${sort}`;
    if (spaceId) url += `&space_id=${encodeURIComponent(spaceId)}`;
    if (type) url += `&type=${encodeURIComponent(type)}`;
    return axios.get(url);
  },
  create: (data) =>
    axios.post(`${API_URL}/posts`, data),
  getComments: (postId) =>
    axios.get(`${API_URL}/posts/${postId}/comments`),
  addComment: (postId, content) =>
    axios.post(`${API_URL}/posts/${postId}/comments`, { content }),
  toggleReaction: (postId, type = 'like') =>
    axios.post(`${API_URL}/posts/${postId}/reactions`, { type }),
  // Q&A vote: value 1 (up), -1 (down), 0 (cancel)
  vote: (postId, value) =>
    axios.post(`${API_URL}/posts/${postId}/vote`, { value }),
  voteComment: (postId, commentId, value) =>
    axios.post(`${API_URL}/posts/${postId}/comments/${commentId}/vote`, { value }),
  // Autor pytania oznacza komentarz jako zaakceptowaną odpowiedź. commentId=null cofa.
  acceptAnswer: (postId, commentId) =>
    axios.post(`${API_URL}/posts/${postId}/accept-answer`, { comment_id: commentId }),
  // Read tracker — używane dla ogłoszeń (type=announcement)
  markRead: (postId) =>
    axios.post(`${API_URL}/posts/${postId}/mark-read`),
  readStats: (postId) =>
    axios.get(`${API_URL}/posts/${postId}/read-stats`),
};

// Marketplace
export const marketplaceApi = {
  list: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return axios.get(`${API_URL}/marketplace/configs${q ? '?' + q : ''}`);
  },
  get: (id, withData = false) =>
    axios.get(`${API_URL}/marketplace/configs/${id}${withData ? '?with_data=true' : ''}`),
  rate: (id, rating) =>
    axios.post(`${API_URL}/marketplace/configs/${id}/rate`, { rating }),
};

// Roadmap / Beta voting
export const roadmapApi = {
  list: () => axios.get(`${API_URL}/marketplace/roadmap`),
  vote: (ideaId) => axios.post(`${API_URL}/marketplace/roadmap/${ideaId}/vote`),
};

// Search (full-text)
export const searchApi = {
  query: (q, limit = 20) => axios.get(`${API_URL}/search`, { params: { q, limit } }),
};

// User settings
export const userSettingsApi = {
  update: (payload) => axios.put(`${API_URL}/users/me/settings`, payload),
};

// Direct Messages (DM 1:1)
export const messagesApi = {
  listConversations: () => axios.get(`${API_URL}/messages/conversations`),
  createConversation: (receiverId) =>
    axios.post(`${API_URL}/messages/conversations`, { receiver_id: receiverId }),
  listMessages: (convId, limit = 100) =>
    axios.get(`${API_URL}/messages/conversations/${convId}/messages`, { params: { limit } }),
  sendMessage: (convId, content) =>
    axios.post(`${API_URL}/messages/conversations/${convId}/messages`, { content }),
  createGroup: (name, participantIds) =>
    axios.post(`${API_URL}/messages/groups`, { name, participant_ids: participantIds }),
  leaveConversation: (convId) =>
    axios.post(`${API_URL}/messages/conversations/${convId}/leave`),
};

// Tickets (private support / questions / requests w community)
export const ticketsApi = {
  list: (communityId, opts = {}) => {
    const params = { community_id: communityId, ...opts };
    return axios.get(`${API_URL}/tickets`, { params });
  },
  get: (id) => axios.get(`${API_URL}/tickets/${id}`),
  create: (payload) => axios.post(`${API_URL}/tickets`, payload),
  update: (id, payload) => axios.patch(`${API_URL}/tickets/${id}`, payload),
  addComment: (id, payload) => axios.post(`${API_URL}/tickets/${id}/comments`, payload),
};

// Notifications
export const notificationsApi = {
  list: (unreadOnly = false, limit = 30) =>
    axios.get(`${API_URL}/notifications`, { params: { unread_only: unreadOnly, limit } }),
  unreadCount: () =>
    axios.get(`${API_URL}/notifications/unread-count`),
  markRead: (id) =>
    axios.post(`${API_URL}/notifications/${id}/read`),
  markAllRead: () =>
    axios.post(`${API_URL}/notifications/mark-all-read`),
};

// Aura Score
export const auraApi = {
  getScore: (userId) =>
    axios.get(`${API_URL}/aura/score/${userId}`),
  // Panel zdrowia zawiera już at_risk_members — osobny endpoint /at-risk nie jest potrzebny w UI
  getCommunityHealth: (communityId) =>
    axios.get(`${API_URL}/aura/community/${communityId}/health`),
};

// Quests
export const questsApi = {
  list: (communityId = null) => {
    let url = `${API_URL}/quests`;
    if (communityId) url += `?community_id=${communityId}`;
    return axios.get(url);
  },
  getMyQuests: () => 
    axios.get(`${API_URL}/quests/my`),
  getMyLevel: () => 
    axios.get(`${API_URL}/quests/level`),
  getMyAchievements: () => 
    axios.get(`${API_URL}/quests/achievements`),
  getLeaderboard: (communityId = null, limit = 50) => {
    let url = `${API_URL}/quests/leaderboard?limit=${limit}`;
    if (communityId) url += `&community_id=${communityId}`;
    return axios.get(url);
  },
};

// Matching
export const matchingApi = {
  getSuggestions: (limit = 10) => 
    axios.get(`${API_URL}/matching/suggestions?limit=${limit}`),
  dismissSuggestion: (suggestionId) => 
    axios.post(`${API_URL}/matching/suggestions/${suggestionId}/dismiss`),
  createConnection: (data) => 
    axios.post(`${API_URL}/matching/connections`, data),
  getConnections: (status = null) => {
    let url = `${API_URL}/matching/connections`;
    if (status) url += `?status=${status}`;
    return axios.get(url);
  },
  updateConnection: (connectionId, status) =>
    axios.put(`${API_URL}/matching/connections/${connectionId}`, { status }),
};

// Media upload (Hetzner Object Storage)
export const mediaApi = {
  upload: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return axios.post(`${API_URL}/media/upload`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Payments / subskrypcje (F2)
export const paymentsApi = {
  listPlans: () => axios.get(`${API_URL}/payments/plans`),
  checkout: (planId) => axios.post(`${API_URL}/payments/checkout-session`, { plan_id: planId }),
  mySubscription: () => axios.get(`${API_URL}/payments/my-subscription`),
};

// Admin — konfiguracja aplikacji (klucze API w UI) + plany
export const adminApi = {
  getConfig: () => axios.get(`${API_URL}/admin/config`),
  updateConfig: (payload) => axios.put(`${API_URL}/admin/config`, payload),
  getPlans: () => axios.get(`${API_URL}/admin/plans`),
  updatePlan: (id, payload) => axios.put(`${API_URL}/admin/plans/${id}`, payload),
};

// Web push (F4)
export const pushApi = {
  vapidKey: () => axios.get(`${API_URL}/push/vapid-key`),
  subscribe: (subscription) => axios.post(`${API_URL}/push/subscribe`, { subscription }),
  unsubscribe: (endpoint) => axios.post(`${API_URL}/push/unsubscribe`, { endpoint }),
};
